import { useState } from "react";
import {
    Phone,
    Mail,
    MapPin,
    CheckCircle2,
    AlertCircle,
} from "lucide-react";
import Seo from "@/components/Seo";
import { Reveal, Eyebrow } from "@/components/Reveal";
import WhatsAppIcon from "@/components/WhatsAppIcon";
import {
    PHONE_DISPLAY,
    PHONE_TEL,
    EMAIL,
    WHATSAPP_URL,
    u,
    IMG,
} from "@/lib/site";

// Web3Forms access key (safe to keep in frontend code).
const WEB3FORMS_ACCESS_KEY = "e8917c16-bb50-4f44-b4a5-4d8128f6c18c";
const WEB3FORMS_URL = "https://api.web3forms.com/submit";

const REQUIREMENTS = [
    "Teak — Round Logs",
    "Teak — Rough Squares",
    "Teak — Sawn Sizes",
    "Softwood — Pine",
    "Softwood — SYP",
    "Softwood — Spruce",
    "Hardwood — Meranti",
    "Hardwood — Malaysian Sal",
    "Hardwood — Wana",
    "Custom / Other Requirement",
];

const EMPTY = {
    full_name: "",
    company_name: "",
    phone: "",
    requirement: "",
    quantity: "",
    website: "",
};

const inputCls =
    "w-full border border-line bg-cream/60 px-4 py-3 text-sm text-ink placeholder:text-mutedw/50 transition-colors focus:border-teak focus:outline-none";

const Field = ({ label, required, error, children, id }) => (
    <div>
        <label
            htmlFor={id}
            className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.14em] text-ink/70"
        >
            {label} {required && <span className="text-teak">*</span>}
        </label>

        {children}

        {error && (
            <p
                className="mt-1.5 text-xs text-red-700"
                role="alert"
                data-testid={`${id}-error`}
            >
                {error}
            </p>
        )}
    </div>
);

export default function Contact() {
    const [form, setForm] = useState(EMPTY);
    const [errors, setErrors] = useState({});
    const [status, setStatus] = useState("idle");

    const set = (key) => (event) => {
        const value = event.target.value;

        setForm((previous) => ({
            ...previous,
            [key]: value,
        }));

        setErrors((previous) => ({
            ...previous,
            [key]: undefined,
        }));
    };

    const validate = () => {
        const validationErrors = {};

        if (form.full_name.trim().length < 2) {
            validationErrors.full_name = "Please enter your full name.";
        }

        if (!/^[+0-9][0-9\s\-()]{6,19}$/.test(form.phone.trim())) {
            validationErrors.phone = "Please enter a valid phone number.";
        }

        if (!form.requirement) {
            validationErrors.requirement = "Please select a requirement.";
        }

        return validationErrors;
    };

    const onSubmit = async (event) => {
        event.preventDefault();

        const validationErrors = validate();
        setErrors(validationErrors);

        if (Object.keys(validationErrors).length > 0) {
            return;
        }

        // Spam trap: bots fill the hidden field. Pretend success, send nothing.
        if (form.website.trim()) {
            setStatus("success");
            return;
        }

        setStatus("submitting");

        try {
            const response = await fetch(WEB3FORMS_URL, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json",
                },
                body: JSON.stringify({
                    access_key: WEB3FORMS_ACCESS_KEY,
                    subject: `New Timber Inquiry: ${form.requirement} (${form.full_name.trim()})`,
                    from_name: "ISHAAN WORLDLINK Website",
                    "Full Name": form.full_name.trim(),
                    "Company Name": form.company_name.trim() || "-",
                    Phone: form.phone.trim(),
                    Requirement: form.requirement,
                    "Quantity (Minimum Truck Load)": form.quantity.trim() || "-",
                }),
            });

            const data = await response.json().catch(() => ({}));

            // Show success only when Web3Forms confirms it.
            if (!response.ok || data.success !== true) {
                console.error("Web3Forms rejected the submission:", data);
                throw new Error("The inquiry could not be submitted.");
            }

            setStatus("success");
        } catch (error) {
            console.error("Web3Forms submission failed:", error);
            setStatus("error");
        }
    };

    return (
        <>
            <Seo
                title="Contact Us | ISHAAN WORLDLINK LLP"
                description="Discuss your timber requirement with ISHAAN WORLDLINK LLP, Gandhidham. Call or WhatsApp +91 98257 23825, or send an inquiry."
            />

            {/* HERO */}
            <section
                className="relative flex min-h-[50svh] items-end overflow-hidden bg-ink"
                data-testid="contact-hero"
            >
                <img
                    src={u(IMG.grainOak, 2000)}
                    alt="Natural teak wood grain texture"
                    className="absolute inset-0 h-full w-full object-cover opacity-50"
                    fetchPriority="high"
                />

                <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/60 to-ink/30" />

                <div className="relative mx-auto w-full max-w-7xl px-4 pb-16 pt-44 sm:px-6 lg:px-8">
                    <Reveal y={16}>
                        <Eyebrow dark>Contact Us</Eyebrow>

                        <h1 className="mt-4 max-w-3xl font-display text-4xl font-semibold leading-[1.04] tracking-tight text-cream sm:text-5xl">
                            LET&apos;S DISCUSS YOUR TIMBER REQUIREMENT.
                        </h1>
                    </Reveal>
                </div>
            </section>

            {/* CONTACT DETAILS AND FORM */}
            <section
                className="bg-cream py-20 sm:py-28"
                data-testid="contact-section"
            >
                <div className="mx-auto grid max-w-7xl gap-10 px-4 sm:px-6 lg:grid-cols-12 lg:px-8">
                    {/* DIRECT CONTACT DETAILS */}
                    <Reveal className="lg:col-span-4">
                        <div className="flex h-full flex-col bg-ink p-8 sm:p-10">
                            <Eyebrow dark>Direct Lines</Eyebrow>

                            <div className="mt-8 flex flex-col gap-6">
                                <a
                                    href={PHONE_TEL}
                                    data-testid="contact-phone-link"
                                    className="group flex items-center gap-4"
                                >
                                    <Phone className="h-5 w-5 shrink-0 text-gold" />

                                    <span>
                                        <span className="block text-[11px] uppercase tracking-[0.2em] text-cream/50">
                                            Call
                                        </span>

                                        <span className="block text-sm font-semibold text-cream transition-colors group-hover:text-gold">
                                            {PHONE_DISPLAY}
                                        </span>
                                    </span>
                                </a>

                                <a
                                    href={WHATSAPP_URL}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="contact-whatsapp-link"
                                    className="group flex items-center gap-4"
                                >
                                    <WhatsAppIcon className="h-5 w-5 shrink-0 text-[#25D366]" />

                                    <span>
                                        <span className="block text-[11px] uppercase tracking-[0.2em] text-cream/50">
                                            WhatsApp
                                        </span>

                                        <span className="block text-sm font-semibold text-cream transition-colors group-hover:text-[#25D366]">
                                            {PHONE_DISPLAY}
                                        </span>
                                    </span>
                                </a>

                                <a
                                    href={`mailto:${EMAIL}`}
                                    data-testid="contact-email-link"
                                    className="group flex items-center gap-4"
                                >
                                    <Mail className="h-5 w-5 shrink-0 text-gold" />

                                    <span>
                                        <span className="block text-[11px] uppercase tracking-[0.2em] text-cream/50">
                                            Email
                                        </span>

                                        <span className="block break-all text-sm font-semibold text-cream transition-colors group-hover:text-gold">
                                            {EMAIL}
                                        </span>
                                    </span>
                                </a>
                            </div>

                            <p className="mt-auto flex items-start gap-2 pt-10 text-xs leading-relaxed text-cream/50">
    <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0" />
    <span>
        Registered Office: A-117, Kutch Arcade Titanium, NH-8/A, Mithirohar,
        Gandhidham (Kachchh) - 370240, Gujarat (IN)
    </span>
</p>
                        </div>
                    </Reveal>

                    {/* CONTACT FORM */}
                    <Reveal delay={0.1} className="lg:col-span-8">
                        <div className="border border-line bg-white p-8 sm:p-10">
                            {status === "success" ? (
                                <div
                                    className="flex flex-col items-start gap-5 py-10"
                                    data-testid="contact-success"
                                >
                                    <CheckCircle2 className="h-10 w-10 text-teak" />

                                    <h2 className="font-display text-2xl font-semibold tracking-tight text-ink sm:text-3xl">
                                        Inquiry received.
                                    </h2>

                                    <p className="max-w-md text-sm leading-relaxed text-mutedw">
                                        Thank you — your requirement has been sent
                                        to our team. For anything urgent, reach us
                                        instantly on WhatsApp at {PHONE_DISPLAY}.
                                    </p>

                                    <button
                                        type="button"
                                        data-testid="contact-send-another-btn"
                                        onClick={() => {
                                            setForm({ ...EMPTY });
                                            setErrors({});
                                            setStatus("idle");
                                        }}
                                        className="mt-2 border border-ink px-6 py-3 text-[13px] font-semibold uppercase tracking-[0.14em] text-ink transition-colors hover:bg-ink hover:text-cream"
                                    >
                                        Send another inquiry
                                    </button>
                                </div>
                            ) : (
                                <form
                                    onSubmit={onSubmit}
                                    noValidate
                                    data-testid="contact-form"
                                >
                                    <div className="grid gap-6 sm:grid-cols-2">
                                        <Field
                                            label="Full Name"
                                            required
                                            error={errors.full_name}
                                            id="contact-full-name"
                                        >
                                            <input
                                                id="contact-full-name"
                                                data-testid="contact-full-name"
                                                type="text"
                                                autoComplete="name"
                                                value={form.full_name}
                                                onChange={set("full_name")}
                                                aria-invalid={!!errors.full_name}
                                                className={inputCls}
                                                placeholder="Your name"
                                            />
                                        </Field>

                                        <Field
                                            label="Company Name"
                                            error={errors.company_name}
                                            id="contact-company-name"
                                        >
                                            <input
                                                id="contact-company-name"
                                                data-testid="contact-company-name"
                                                type="text"
                                                autoComplete="organization"
                                                value={form.company_name}
                                                onChange={set("company_name")}
                                                className={inputCls}
                                                placeholder="Your company"
                                            />
                                        </Field>

                                        <Field
                                            label="Phone Number"
                                            required
                                            error={errors.phone}
                                            id="contact-phone"
                                        >
                                            <input
                                                id="contact-phone"
                                                data-testid="contact-phone"
                                                type="tel"
                                                autoComplete="tel"
                                                value={form.phone}
                                                onChange={set("phone")}
                                                aria-invalid={!!errors.phone}
                                                className={inputCls}
                                                placeholder="+91 ..."
                                            />
                                        </Field>

                                        <Field
                                            label="Requirement / Product"
                                            required
                                            error={errors.requirement}
                                            id="contact-requirement"
                                        >
                                            <select
                                                id="contact-requirement"
                                                data-testid="contact-requirement"
                                                value={form.requirement}
                                                onChange={set("requirement")}
                                                aria-invalid={!!errors.requirement}
                                                className={inputCls}
                                            >
                                                <option value="">
                                                    Select a product
                                                </option>

                                                {REQUIREMENTS.map((requirement) => (
                                                    <option
                                                        key={requirement}
                                                        value={requirement}
                                                    >
                                                        {requirement}
                                                    </option>
                                                ))}
                                            </select>
                                        </Field>

                                        <Field
                                            label="Quantity (Minimum Truck Load)"
                                            error={errors.quantity}
                                            id="contact-quantity"
                                        >
                                            <input
                                                id="contact-quantity"
                                                data-testid="contact-quantity"
                                                type="text"
                                                value={form.quantity}
                                                onChange={set("quantity")}
                                                className={inputCls}
                                                placeholder="e.g. 900 CFT"
                                            />
                                        </Field>
                                    </div>

                                    {/* HIDDEN SPAM-TRAP FIELD */}
                                    <div
                                        className="absolute -left-[9999px] top-auto h-px w-px overflow-hidden"
                                        aria-hidden="true"
                                    >
                                        <label>
                                            Website
                                            <input
                                                type="text"
                                                name="website"
                                                tabIndex={-1}
                                                autoComplete="off"
                                                value={form.website}
                                                onChange={set("website")}
                                            />
                                        </label>
                                    </div>

                                    {status === "error" && (
                                        <div
                                            className="mt-6 flex items-start gap-3 border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
                                            role="alert"
                                            data-testid="contact-error"
                                        >
                                            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />

                                            <p>
                                                We couldn&apos;t send your inquiry just
                                                now. Please try again, or reach us
                                                directly on WhatsApp at {PHONE_DISPLAY}.
                                            </p>
                                        </div>
                                    )}

                                    <button
                                        type="submit"
                                        data-testid="contact-form-submit-button"
                                        disabled={status === "submitting"}
                                        className="mt-8 inline-flex items-center gap-2 bg-teak px-8 py-4 text-[13px] font-semibold uppercase tracking-[0.14em] text-cream transition-colors duration-300 hover:bg-teakdark disabled:cursor-not-allowed disabled:opacity-60"
                                    >
                                        {status === "submitting"
                                            ? "Sending…"
                                            : "Submit Inquiry"}
                                    </button>
                                </form>
                            )}
                        </div>
                    </Reveal>
                </div>
            </section>
        </>
    );
}
